// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 */
export function trapFocus(container) {
  // Remove any previous trap first.
  removeTrapFocus();

  /**
   * IMPORTANT:
   * Do NOT scan every focusable element when the drawer opens.
   * We only need that list if the user actually presses Tab.
   */

  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(container);

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeEl = document.activeElement;

    // Tab forward from last item → first item
    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first?.focus({ preventScroll: true });
    }

    // Shift + Tab from first item/container → last item
    else if (
      event.shiftKey &&
      (activeEl === first || activeEl === container)
    ) {
      event.preventDefault();
      last?.focus({ preventScroll: true });
    }
  };

  trapFocusHandlers.focusin = (event) => {
    // Nothing to do if focus is already inside the drawer.
    if (
      !(event.target instanceof Node) ||
      container.contains(event.target)
    ) {
      return;
    }

    event.stopPropagation();

    /*
     * Only scan for focusable elements if focus actually
     * escapes the drawer.
     */
    const focusable = getFocusableElements(container);
    const first = focusable[0];

    first?.focus({ preventScroll: true });
  };

  document.addEventListener(
    'keydown',
    trapFocusHandlers.keydown,
    true
  );

  document.addEventListener(
    'focusin',
    trapFocusHandlers.focusin,
    true
  );

  /*
   * DO NOT call container.focus() here.
   * theme-drawer.js already sets initial focus.
   */
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus() {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
